/**
 * The full results page (brief 06, B4): a shareable `/search?q=&type=`, tabs All and one per
 * group, each hit with its breadcrumb *project → building → floor → zone*.
 *
 * The first screen is the same stream the overlay reads (`useSearchStream`), so a page opened
 * from ⌘Enter and the overlay it came from show the same rows in the same order. "Load more" is
 * per group, on that group's tab, through `GET /search`'s keyset cursor (`SearchResultsGroup`).
 *
 * Not built here, deliberately: the brief's project and date filters. `GET /search` takes no
 * such parameter, and a filter the server does not apply would be a client-side scan of a page
 * pretending to be a filter.
 */

import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { EmptyState, InlineError, Input, Screen, ScreenHead, SkeletonRows } from '@/components';
import { useMessages } from '@/lib/i18n';

import { MAX_QUERY, normaliseQuery, SEARCH_GROUPS } from './api';
import type { SearchGroupKey, SearchHit } from './api';
import { groupViews, isEmptyAnswer } from './model';
import { searchProblemKey } from './problems';
import { GROUP_LABEL } from './SearchGroup';
import { SearchResultsGroup } from './SearchResultsGroup';
import styles from './Search.module.css';
import { useSearchStream } from './useSearchStream';

export interface SearchResultsPageProps {
  /** `?q=` as it is in the address. */
  readonly q: string;
  /** `?type=`; `null` is the All tab. */
  readonly group: SearchGroupKey | null;
  /** The box settled on a new query — the address follows, replacing rather than pushing. */
  readonly onQueryChange: (q: string) => void;
  readonly onGroupChange: (group: SearchGroupKey | null) => void;
}

export function SearchResultsPage({ q, group, onQueryChange, onGroupChange }: SearchResultsPageProps) {
  const t = useMessages('search');
  const navigate = useNavigate();
  const [draft, setDraft] = useState(q);
  const { settled, state } = useSearchStream(draft);

  // The box → the address, once per settled query. Keyed on `settled` changing rather than on
  // the two disagreeing: an address changed from outside (⌘Enter in the overlay, Back) must not
  // be written over by the query this box settled on before it.
  const reported = useRef(settled);
  useEffect(() => {
    if (reported.current === settled) return;
    reported.current = settled;
    if (settled !== normaliseQuery(q)) onQueryChange(settled);
  }, [settled, q, onQueryChange]);

  // The address → the box, when something else changed it.
  const read = useRef(q);
  useEffect(() => {
    if (read.current === q) return;
    read.current = q;
    if (normaliseQuery(q) !== settled) setDraft(q);
  }, [q, settled]);

  const open = (hit: SearchHit): void => {
    void navigate({ href: hit.route });
  };

  const views = groupViews(state).filter((view) => group === null || view.group === group);

  let body: ReactNode;
  if (state.phase === 'idle') {
    body = (
      <EmptyState
        variant="screen"
        // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
        explains={t('page.start.body')}
        title={t('page.start.title')}
      />
    );
  } else if (state.phase === 'waiting') {
    body = <SkeletonRows label={t('page.loading')} rows={5} columns={[16, '48%']} rowHeight={56} />;
  } else if (isEmptyAnswer(state, views)) {
    body = (
      <EmptyState
        variant="list"
        // TODO(-014): placeholder sentence in the brief's voice; wave 3 writes the real one.
        explains={t('empty.explains')}
        title={t('empty.title', { q: settled })}
        body={t('empty.body')}
      />
    );
  } else {
    body = views.map((view) => (
      <SearchResultsGroup
        key={`${settled}:${view.group}`}
        q={settled}
        view={view}
        single={group !== null}
        onShowAll={() => {
          onGroupChange(view.group);
        }}
        onOpen={open}
      />
    ));
  }

  return (
    <Screen
      head={<ScreenHead level={1} title={t('page.title')} subtitle={t('page.subtitle')} />}
      toolbar={
        <div className={styles.pageToolbar}>
          <div className={styles.pageBox}>
            <Input
              label={t('page.label')}
              labelHidden
              type="search"
              value={draft}
              maxLength={MAX_QUERY}
              placeholder={t('overlay.placeholder')}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
            />
          </div>
          <div className={styles.tabs} role="tablist" aria-label={t('page.tabs')}>
            {[null, ...SEARCH_GROUPS].map((one) => (
              <button
                key={one ?? 'all'}
                type="button"
                role="tab"
                aria-selected={group === one}
                className={[styles.tab, group === one ? styles.tabActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  onGroupChange(one);
                }}
              >
                {one === null ? t('page.tab.all') : t(GROUP_LABEL[one])}
              </button>
            ))}
          </div>
        </div>
      }
    >
      <div className={styles.page}>
        {state.semanticUnavailable ? <p className={styles.pageNote}>{t('unavailable')}</p> : null}
        {state.problem === null ? null : (
          <InlineError variant="block">{t(searchProblemKey(state.problem))}</InlineError>
        )}
        {body}
      </div>
    </Screen>
  );
}
